import { useRef, useState } from "react"
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Trash2,
  Upload,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useQueryClient } from "@tanstack/react-query"

import { useContracts } from "@/features/contracts/hooks/use-contracts"
import type { LotDetail, LotQCLog, QCDecision } from "@/features/lots/api/types"
import {
  useDeleteQCLog,
  useDeleteQCLogDoc,
  usePatchQCLog,
} from "@/features/lots/hooks/use-lot-mutations"
import { lotKeys } from "@/features/lots/hooks/use-lots"
import { decisionTone } from "@/features/lots/lib/lot-status"
import { uploadDocument } from "@/features/documents/lib/upload-helpers"
import { extractApiErrorMessage } from "@/lib/api/client"
import { toast } from "sonner"

interface QCLogRowProps {
  lot: LotDetail
  log: LotQCLog
}

export function QCLogRow({ lot, log }: QCLogRowProps) {
  const { t } = useTranslation("lots")
  const [open, setOpen] = useState(false)
  const { data: contracts } = useContracts()

  const tone = decisionTone(log.decision)

  // Look up the contract by id from the contracts list (the QC log row
  // itself only carries `contractId`). If the contract isn't found
  // (deleted, or the user has no read access), we fall back to the
  // log's kind label.
  const contract = log.contractId
    ? (contracts ?? []).find((c) => Number(c.id) === log.contractId)
    : null
  const kindLabelKey =
    log.kind === "LAB"
      ? "detail.qcModal.kindLab"
      : log.kind === "CLIENT"
        ? "detail.qcModal.kindClient"
        : "detail.qcModal.kindOther"
  const heading =
    contract?.clientName ??
    (contract?.contractNumber
      ? `Contract ${contract.contractNumber}`
      : t(kindLabelKey))

  return (
    <div className="bg-card overflow-hidden rounded-md border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hover:bg-muted/40 flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">{heading}</span>
            {contract?.contractNumber ? (
              <span className="text-muted-foreground font-mono text-[10px]">
                · {contract.contractNumber}
              </span>
            ) : null}
          </div>
          <div className="text-muted-foreground mt-0.5 text-[10px] tabular-nums">
            {log.created_at ? String(log.created_at).slice(0, 16) : "—"}
          </div>
        </div>
        <StatusBadge tone={tone}>
          {t(`detail.decision.${log.decision}`)}
        </StatusBadge>
        {open ? (
          <ChevronUp className="text-muted-foreground size-4" />
        ) : (
          <ChevronDown className="text-muted-foreground size-4" />
        )}
      </button>

      {open ? <QCLogBody lot={lot} log={log} /> : null}
    </div>
  )
}

function QCLogBody({ lot, log }: { lot: LotDetail; log: LotQCLog }) {
  const { t } = useTranslation("lots")
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const patch = usePatchQCLog(lot.id)
  const removeLog = useDeleteQCLog(lot.id)
  const removeDoc = useDeleteQCLogDoc(lot.id)
  const queryClient = useQueryClient()

  const setDecision = (decision: QCDecision) => {
    patch.mutate({ logId: log.id, payload: { decision } })
  }

  // Uploading a document via /exporter/documents (level=lotQc) appends
  // the entry to the QC log's `documents` JSONB server-side — there's no
  // separate FE call to make. We just invalidate the lot detail query
  // so the new document shows up in the list.
  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await uploadDocument({
        file,
        level: "lotQc",
        title: file.name,
        lotId: String(lot.id),
        logId: String(log.id),
      })
      toast.success(t("toast.docUploaded"))
      queryClient.invalidateQueries({ queryKey: lotKeys.details(lot.id) })
    } catch (err) {
      toast.error(extractApiErrorMessage(err))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ""
    }
  }

  return (
    <div className="bg-muted/30 border-t px-4 py-4">
      {log.reason ? (
        <div className="text-destructive text-xs">
          <span className="text-muted-foreground mr-2 text-[10px] font-medium uppercase tracking-wide">
            {t("detail.qc.reasonLabel")}
          </span>
          {log.reason}
        </div>
      ) : null}

      {log.note ? (
        <div className="text-foreground/90 mt-2 text-xs">
          <span className="text-muted-foreground mr-2 text-[10px] font-medium uppercase tracking-wide">
            {t("detail.qc.noteLabel")}
          </span>
          {log.note}
        </div>
      ) : null}

      <div className="mt-3">
        <div className="text-muted-foreground mb-1.5 text-[10px] font-medium uppercase tracking-wide">
          {t("detail.qc.decision")}
        </div>
        <div className="flex w-48">
          <Select
            value={log.decision}
            onValueChange={(v) => setDecision(v as QCDecision)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACCEPTED">
                {t("detail.decision.ACCEPTED")}
              </SelectItem>
              <SelectItem value="REJECTED">
                {t("detail.decision.REJECTED")}
              </SelectItem>
              <SelectItem value="PENDING">
                {t("detail.decision.PENDING")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
            {t("detail.qc.documents")}
            <span className="ml-1 tabular-nums">
              ({log.documents.length})
            </span>
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={onUpload}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <Upload className="size-3.5" />
            {t("detail.qc.uploadDoc")}
          </Button>
        </div>

        {log.documents.length === 0 ? (
          <div className="text-muted-foreground/70 text-xs italic">
            {t("detail.qc.noDocs")}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {log.documents.map((d) => (
              <div
                key={d.key}
                className="bg-card flex items-center gap-2 rounded-md border px-2 py-1 text-xs"
              >
                <FileText className="text-muted-foreground size-3.5 flex-shrink-0" />
                <span className="flex-1 truncate">{d.title ?? d.key}</span>
                {d.date ? (
                  <span className="text-muted-foreground tabular-nums">
                    {String(d.date).slice(0, 10)}
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    removeDoc.mutate({
                      logId: log.id,
                      storedKey: d.key,
                    })
                  }
                  className="text-muted-foreground hover:text-destructive rounded p-0.5"
                  aria-label="Remove"
                >
                  <Trash2 className="size-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end border-t pt-3">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => removeLog.mutate(log.id)}
        >
          <Trash2 className="size-3.5" />
          {t("detail.qc.removeLog")}
        </Button>
      </div>
    </div>
  )
}
